import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CampaignDetailHolder from "../../styles/campaignDetailStyle";
import useCampaignDetailActions from "../../hooks/useCampaignDetailActions";
import useRealSpots from "../../hooks/useRealSpots";
import useEditCampaign from "../../hooks/useEditCampaign";
import useCreateProposal from "../../hooks/useCreateProposal";
import EditModeModal from "./EditModeModal";
import EditDatesModal from "./EditDatesModal";
import CampaignAudienceModal from "./CampaignAudienceModal";
import ReplicateCampaignModal from "./ReplicateCampaignModal";
import ConsumoDiarioCard from "./ConsumoDiarioCard";
import CampaignDetailHeader from "./CampaignDetailHeader";
import CampaignSummaryCard from "./CampaignSummaryCard";
import PaymentsCard from "./PaymentsCard";
import CampaignMapCard from "./CampaignMapCard";
import ScreenAdsCard from "./ScreenAdsCard";
import CampaignCreatingCard from "./CampaignCreatingCard";
import { isCpmiCompany } from "../../../../../services/utils";
import { isCampaignBeingCreated } from "../../../../../services/campaignManager";
import api from "../../../../../services/apiInstance";

const CREATING_CAMPAIGN_POLL_MS = 10000;

const CampaignDetail = (props) => {
  const { t } = useTranslation();
  const campaignId = props.match && props.match.params && props.match.params.id;

  useEffect(() => {
    if (!props.user) {
      if (props.setShouldPromptLogin) {
        props.setShouldPromptLogin(true, window.location.pathname);
      }
      if (props.history) props.history.push("/");
      return;
    }
    if (campaignId) {
      props.getCampaign(campaignId);
      props.getCampaignPayments(campaignId);
      props.getCampaignStats(campaignId);
      props.getConversionRates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const campaign = props.currentCampaign;
  const loaded = campaign && String(campaign.id) === String(campaignId);
  const mode = loaded && campaign.status === "proposal" ? "proposal" : "campaign";
  const isCpmi = isCpmiCompany(props.user);
  const isProposal = mode === "proposal";
  const isCreating = !!loaded && isCampaignBeingCreated(campaign);

  useEffect(() => {
    if (!isCreating) return undefined;
    let active = true;
    const interval = setInterval(async () => {
      const resp = await api.getCampaign(campaignId);
      if (active && resp.ok && !isCampaignBeingCreated(resp.data)) {
        clearInterval(interval);
        window.location.reload();
      }
    }, CREATING_CAMPAIGN_POLL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [isCreating, campaignId]);

  const canEditCampaign =
    !!loaded &&
    !!(
      props.user &&
      props.user.company &&
      String(props.user.company.id) === String(campaign.company_id)
    ) &&
    !(props.user && props.user.holding && props.user.holding.id === 50);

  const actions = useCampaignDetailActions({
    campaign: loaded ? campaign : null,
    token: props.token,
    history: props.history,
    getProposalRequest: props.getProposalRequest,
    proposalToken: props.proposalToken,
    loadingProposal: props.loadingProposal,
    getReportRequest: props.getReportRequest,
    reportToken: props.reportToken,
    loadingReport: props.loadingReport,
    editCampaign: props.editCampaign,
  });

  const realSpots = useRealSpots(loaded ? campaign : null, {
    getSingleReportRequest: props.getSingleReportRequest,
    reportSingleData: props.reportSingleData,
    loadingSingleReport: props.loadingSingleReport,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [datesModalOpen, setDatesModalOpen] = useState(false);
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [replicateModalOpen, setReplicateModalOpen] = useState(false);

  useEffect(() => {
    if (
      loaded &&
      props.location &&
      props.location.search &&
      new URLSearchParams(props.location.search).get("edit") === "1" &&
      canEditCampaign &&
      !isCreating
    ) {
      setEditModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const openAudience = () => {
    setAudienceModalOpen(true);
    if (loaded && campaign.id) {
      props.getProposalAudience({ campaign_id: campaign.id });
    }
  };
  const editHook = useEditCampaign(loaded ? campaign : null, {
    history: props.history,
    cart: props.cart,
    categories: props.categories,
    fetchingDisplaysInfo: props.fetchingDisplaysInfo,
    getCategories: props.getCategories,
    clearCampaignData: props.clearCampaignData,
    setDefaultContent: props.setDefaultContent,
    setToleranceResolution: props.setToleranceResolution,
    setToleranceResolutionInit: props.setToleranceResolutionInit,
    removeDisplayFromCart: props.removeDisplayFromCart,
    getDisplaysInfoRequest: props.getDisplaysInfoRequest,
    setDates: props.setDates,
    setCampaignName: props.setCampaignName,
    setProgrammatic: props.setProgrammatic,
    setBudget: props.setBudget,
    updateBundles: props.updateBundles,
    addCustomContentFromCampaign: props.addCustomContentFromCampaign,
    updateCustomContentRequestActive: props.updateCustomContentRequestActive,
    activateEditCampaign: props.activateEditCampaign,
    setNewVersionCampaign: props.setNewVersionCampaign,
    t,
  });

  const proposalHook = useCreateProposal(loaded ? campaign : null, {
    history: props.history,
    fetchingDisplaysInfo: props.fetchingDisplaysInfo,
    clearCampaignData: props.clearCampaignData,
    setClient: props.setClient,
    setBrand: props.setBrand,
    setDates: props.setDates,
    setProgrammatic: props.setProgrammatic,
    setBudget: props.setBudget,
    getDisplaysInfoRequest: props.getDisplaysInfoRequest,
  });

  return (
    <CampaignDetailHolder>
      <CampaignDetailHeader
        campaign={loaded ? campaign : null}
        mode={mode}
        user={props.user}
        history={props.history}
        isPaused={actions.isPaused}
        onViewProposal={actions.viewProposal}
        onCreateProposal={() => proposalHook.startCreateProposal()}
        onGoToCMS={actions.goToCMS}
        onViewReport={actions.viewReport}
        onOpenAudience={openAudience}
        onEdit={() => setEditModalOpen(true)}
        canEdit={canEditCampaign}
        onTogglePause={actions.togglePause}
        onRelaunch={() => setReplicateModalOpen(true)}
        actionsDisabled={isCreating}
      />

      {isCreating ? (
        <div className="campaignDetail__panel">
          <CampaignCreatingCard campaign={campaign} mode={mode} history={props.history} />
        </div>
      ) : loaded ? (
        <div className="campaignDetail__panel">
          <CampaignSummaryCard
            campaign={campaign}
            mode={mode}
            realSpots={realSpots}
            isCpmi={isCpmi}
            onEditDates={canEditCampaign ? () => setDatesModalOpen(true) : null}
          />
          <PaymentsCard
            campaign={campaign}
            defaultOpen={!isProposal}
            payments={props.payments}
            deletingPayment={props.deletingPayment}
            deletePayment={props.deletePayment}
            isSuperUser={props.isSuperUser}
            user={props.user}
            history={props.history}
          />
          {!isProposal && (
            <ConsumoDiarioCard
              stats={props.campaignStats}
              loading={props.loadingStats}
              campaign={campaign}
              conversionRates={props.conversionRates}
            />
          )}
          <CampaignMapCard campaign={campaign} isCpmi={isCpmi} />
          <ScreenAdsCard
            campaign={campaign}
            mode={mode}
            reportSingleData={props.reportSingleData}
            loadingSingleReport={props.loadingSingleReport}
            user={props.user}
          />
          <EditModeModal
            visible={editModalOpen}
            campaign={campaign}
            loading={editHook.loadingEdit}
            onCancel={() => setEditModalOpen(false)}
            onConfirm={editHook.startEdit}
          />

          <EditDatesModal
            visible={datesModalOpen}
            campaign={campaign}
            editCampaignDate={props.editCampaignDate}
            isEditingCampaignDate={props.isEditingCampaignDate}
            errors={props.editCampaignDateErrors}
            onCancel={() => setDatesModalOpen(false)}
          />

          <CampaignAudienceModal
            visible={audienceModalOpen}
            displays={campaign.contents_displays}
            proposalAudience={props.proposalAudience}
            onClose={() => setAudienceModalOpen(false)}
          />

          <ReplicateCampaignModal
            visible={replicateModalOpen}
            campaignId={campaign.id}
            name={campaign.name}
            to_date={campaign.start_date}
            end_date={campaign.end_date}
            currentCampaign={campaign}
            replicateCampaign={props.replicateCampaign}
            replicateCampaignId={props.replicateCampaignId}
            replicatingCampaign={props.replicatingCampaign}
            onCancel={() => setReplicateModalOpen(false)}
          />
        </div>
      ) : (
        <div className="campaignDetail__placeholder">{t("Loading")}</div>
      )}
    </CampaignDetailHolder>
  );
};

export default CampaignDetail;
